import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import {
    getCreditLimit,
    getNextRepaymentAmount,
    getOutstandingPrincipal,
    getProductTypeFromURL
} from '../accountsSelector';
import styles from './OverdueBar.module.css';
import messages from './OverdueView.messages';
import { RootState } from '@iwProvider/configureStore';

const mapStateToProps = (state: RootState) => {
    const productType = getProductTypeFromURL();
    return {
        creditLimit: getCreditLimit(state, productType),
        currentPrincipal: getOutstandingPrincipal(state, productType),
        overduePaymentAmount: getNextRepaymentAmount(state, productType)
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const OverdueProgressBar = ({
    creditLimit,
    overduePaymentAmount,
    currentPrincipal
}: PropsFromRedux) => {
    const [overduePosition, setOverduePosition] = useState(0);
    const [overdueWidth, setOverdueWidth] = useState(0);

    useEffect(() => {
        if (overduePaymentAmount) {
            const principalMinusOverdue =
                currentPrincipal - overduePaymentAmount;

            if (principalMinusOverdue > 0) {
                setOverduePosition((principalMinusOverdue / creditLimit) * 100);
                setOverdueWidth((overduePaymentAmount / creditLimit) * 100);
            } else {
                setOverduePosition(0);
                setOverdueWidth((currentPrincipal / creditLimit) * 100);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creditLimit, overduePaymentAmount]);

    const endOfProgressBar = overduePosition + overdueWidth >= 99;
    const beginningOfProgressBar = overduePosition === 0;
    const overdueBarClassName = classNames(
        styles.overdueProgressBar,
        endOfProgressBar && !beginningOfProgressBar && styles.endOfProgressBar,
        beginningOfProgressBar &&
            !endOfProgressBar &&
            styles.beginningOfProgressBar,
        endOfProgressBar && beginningOfProgressBar && styles.allOverdue
    );

    const OFFSET_TO_COVER_PROGRESS_BAR = 0.3;

    return (
        <div
            className={overdueBarClassName}
            data-testid="overdue-bar"
            style={{
                left: `${overduePosition - OFFSET_TO_COVER_PROGRESS_BAR}%`,
                width: `${overdueWidth + OFFSET_TO_COVER_PROGRESS_BAR}%`
            }}
        >
            <div className={styles.overduePill}>
                <div className={styles.overdueLine} />
                <div className={styles.title}>
                    <FormattedMessage {...messages.overdue} />
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(OverdueProgressBar);
